import ProductDetail from "@/components/ProductDetail";
import RelatedProductsSections from "@/components/RelatedProductsSections";
import SEO, { SEOProps } from "@/components/SEO";
import GridOutline from "@/components/GridOutline";
import {
  filterDataToSingleItem,
  usePreviewSubscription,
} from "@/lib/sanity/client";
import { mainLayoutQuery, settingsQuery } from "@/lib/sanity/queries";
import { getClient } from "@/lib/sanity/server";
import { mergeWith } from "lodash";
import { GetStaticPaths, GetStaticProps, NextPage } from "next";
import { groq } from "next-sanity";
import { useEffect, useMemo } from "react";

type UrlQuery = {
  slug: string;
};

type QueryData = {
  _id: string;
  [x: string]: any;
};

type Props = {
  preview: boolean;
  data: {
    page: QueryData;
    query: string;
    queryParams: {
      slug: string;
      locale?: string;
      defaultLocale?: string;
    };
  };
  settings: any;
  layout: any;
};

export const getStaticPaths: GetStaticPaths<UrlQuery> = async () => {
  const allSlugsQuery = groq`*[_type == "product" && defined(slug.current)][1..20].slug.current`;
  const products = await getClient().fetch<string[]>(allSlugsQuery);

  return {
    paths: products?.map((slug) => "/products/" + slug),
    fallback: "blocking",
  };
};

const pageQuery = groq`*[_type == "product" && slug.current == $slug][0] {
    ...,
    seo {
      "metaTitle": coalesce(metaTitle[$locale], metaTitle[$defaultLocale]),
      "metaDesc": coalesce(metaDesc[$locale], metaDesc[$defaultLocale]),
      "shareTitle": coalesce(shareTitle[$locale], shareTitle[$defaultLocale]),
      "shareDesc": coalesce(shareDesc[$locale], shareDesc[$defaultLocale]),
      shareGraphic {
        asset->{
          ...,
          "alt": coalesce(alt[$locale], alt[$defaultLocale])
        }
      }
    },
    thumbnail {
      "alt": coalesce(alt[$locale], alt[$defaultLocale]),
      asset-> {
        url,
        metadata,
      }
    },
    "typeAttribute" : parameters[attribute._ref == "product-type"] {
      values,
      attribute-> {
        _id, name
      }
    },
    parameters[attribute->.visible == true] {
      values,
      attribute-> {
        _id, name, unit
      }
    },
    "correlatedParameters": parameters[] {
      values,
      "attributeKey": attribute._ref,
    },
    "related": *[_type == "related"] | order(orderRank) {
      _id,
      "title": coalesce(title[$locale], title[$defaultLocale]),
      "rules": rules[] {
        "attributeQuery": attributeQuery._ref,
        "attributeProduct": attributeProduct._ref,
        value,
      }
  }
}
`;

export const getStaticProps: GetStaticProps<Props, UrlQuery> = async ({
  locale = "it",
  defaultLocale = "it",
  params,
  preview = false,
}) => {
  const slug = params?.slug;
  if (!slug) return { notFound: true };

  const queryParams = { slug, locale, defaultLocale };
  const data = await getClient(preview).fetch<QueryData | null>(
    pageQuery,
    queryParams
  );
  // Escape hatch, if our query failed to return data
  if (!data) return { notFound: true };

  // Helper function to reduce all returned documents down to just one
  const page = filterDataToSingleItem(data, preview);

  const [settings, layout] = await Promise.all([
    // Get site settings
    getClient(false).fetch(settingsQuery, {
      locale,
      defaultLocale,
    }),
    // Get Layout data
    getClient(false).fetch(mainLayoutQuery, {
      locale,
      defaultLocale,
    }),
  ]);

  return {
    props: {
      // Pass down the "preview mode" boolean to the client-side
      preview,
      // Pass down the initial content, and our query
      data: { page, query: pageQuery, queryParams },
      settings,
      layout,
    },
    revalidate: 60,
  };
};

const Product: NextPage<Props> = ({ data, settings, preview }) => {
  const { data: previewData, error } = usePreviewSubscription(data.query, {
    params: data.queryParams,
    // The hook will return this on first render
    // This is why it's important to fetch *draft* content server-side!
    initialData: data.page,
    // The passed-down preview context determines whether this function does anything
    enabled: preview,
  });

  useEffect(() => {
    if (error) console.error(error);
  }, [error]);

  // Client-side uses the same query, so we may need to filter it down again
  const product = filterDataToSingleItem(previewData, preview);

  // Merge page seo with general seo
  const seo = useMemo<SEOProps>(() => {
    if (typeof product.seo !== "object") return {};
    if (typeof settings?.seo !== "object") return {};
    return mergeWith(settings.seo, product.seo, (a, b) =>
      b === null ? a : undefined
    );
  }, [product.seo, settings?.seo]);

  const slug = data?.queryParams?.slug;

  return (
    <>
      <SEO {...seo} slug={slug} />
      <ProductDetail
        key={product._id || slug} // Make sure component rerenders on every page change
        product={product as any}
      />
      <RelatedProductsSections product={product} sections={product.related} />
      <GridOutline />
    </>
  );
};

export default Product;
